import { cacheExchange } from "@urql/exchange-graphcache";
import { GetFilesCountQuery, GetFilesQuery } from "./features/file/file.queries";
import { GetConditionCountQuery, GetConditionsQuery } from "./features/condition/condition.queries";
import { GetInsuranceCountQuery, GetInsurancesQuery } from "./features/insurance/insurance.queries";
import { GetSubScriptionCountsQuery } from "./graphql/queries/layout.queries";
import { GetLocationCountQuery, GetLocationsQuery } from "./features/location/location.queries";
import { GetOrganizationCountQuery, GetOrganizationsQuery } from "./features/organization/organization.queries";
import { GetPostalCodeCountQuery, GetPostalCodesQuery } from "./features/postal-code/postalCode.queries";
import { PriorityEntryModalQuery } from "./graphql/queries/drawer.queries";
import { GetPriorityNotesQuery } from "./features/priority-note/priorityNote.queries";
import { GetPriorityStatusesQuery } from "./features/priority-status/priorityStatus.queries";
import { GetPriorityViewsQuery } from "./features/priority-view/priorityView.queries";
import { GetReferralCountQuery, GetReferralsQuery } from "./features/priority-referral/priorityReferral.queries";
import { GetTherapyCategoriesAndSubCategoriesQuery } from "./features/therapy/therapy.queries";
import { GetWebsiteLeadCountQuery, GetWebsiteLeadsQuery } from "./features/website-lead/websiteLead.queries";
import { GetChatRoomsQuery } from "./graphql/queries/chatRoom.queries";
import { GetChatMessagesQuery } from "./graphql/queries/chatMessage.queries";
export const graphcacheExchange = cacheExchange({
    keys: {
        AgeYears: () => null,
        ChatMessage: (data) => data.chatMessageId ? String(data.chatMessageId) : null,
        ChatMessagePage: () => null,
        ChatMessageQueries: () => null,
        ChatMessageMutations: () => null,
        ChatMessageUpdate: () => null,
        ChatMention: () => null,
        ChatPin: (data) => data.chatPinId ? String(data.chatPinId) : null,
        ChatReaction: () => null,
        ChatRoom: (data) => data.chatRoomId ? String(data.chatRoomId) : null,
        ChatRoomGroup: (data) => data.chatRoomGroupId ? String(data.chatRoomGroupId) : null,
        ChatRoomGroupAssignment: () => null,
        ChatRoomGroupMutations: () => null,
        ChatRoomMember: () => null,
        ChatRoomMutations: () => null,
        ChatRoomQueries: () => null,
        ChatRoomUpdate: () => null,
        ChatSearchResult: () => null,
        Condition: (data) => String(data.conditionId),
        ConditionQueries: () => null,
        EmailSetting: (data) => String(data.emailSettingId),
        EmailSettingsQueries: () => null,
        File: (data) => String(data.fileId),
        FileQueries: () => null,
        ImageListItem: () => null,
        Insurance: (data) => String(data.insuranceId),
        InsuranceQueries: () => null,
        ListItem: () => null,
        Location: (data) => String(data.locationId),
        LocationQueries: () => null,
        Organization: (data) => String(data.organizationId),
        OrganizationQueries: () => null,
        OrganizationSettings: () => null,
        OrganizationSettingsIntakeForm: () => null,
        Patient: (data) => String(data.patientId),
        PatientQueries: () => null,
        PostalCode: (data) => String(data.postalCode),
        PriorityAttribute: (data) => String(data.priorityAttributeId),
        // Container types - no stable ID, don't normalize
        PriorityEmailRecordMutations: () => null,
        PriorityEmailRecordQueries: () => null,
        // Some queries (like GetPriorityEmailRecords) don't always select
        // priorityEntryId. When it's missing we must return null here so that
        // graphcache doesn't collapse every result into a single
        // "PriorityEntry:undefined" entity.
        PriorityEntry: (data) => data.priorityEntryId ? String(data.priorityEntryId) : null,
        PriorityEntryQueries: () => null,
        PriorityInvite: (data) => String(data.priorityInviteId),
        PriorityInviteMutations: () => null,
        PriorityInviteQueries: () => null,
        PriorityLocation: () => null,
        PriorityNote: (data) => String(data.priorityNoteId),
        PriorityNoteQueries: () => null,
        PriorityReferral: (data) => String(data.priorityReferralId),
        PriorityReferralQueries: () => null,
        PrioritySearch: () => null,
        PrioritySearchSort: () => null,
        PrioritySearchWhereFilter: () => null,
        PrioritySearchWhereFilterObject: () => null,
        PrioritySearchUUIDWhereFilterObject: () => null,
        PrioritySearchWhereFilterQueries: () => null,
        PriorityStatus: (data) => String(data.priorityStatusId),
        PriorityStatusQueries: () => null,
        PriorityView: (data) => String(data.priorityViewId),
        PriorityViewQueries: () => null,
        RelatedEntry: () => null,
        SimpleEmailTemplate: (data) => String(data.simpleEmailTemplateId),
        SimpleEmailTemplateQueries: () => null,
        TimeWaitingDate: () => null,
        TherapyCategory: (data) => String(data.therapyCategoryId),
        TherapyCategoryQueries: () => null,
        TherapySubCategory: (data) => String(data.therapySubCategoryId),
        TherapySubCategoryQueries: () => null,
        User: (data) => String(data.userId),
        UserQueries: () => null,
        WebsiteLead: (data) => String(data.websiteLeadId),
        WebsiteLeadQueries: () => null
    },
    updates: {
        Mutation: {
            // When we masquerade into (or out of) an organization, the session
            // and permissions effectively change users. To avoid leaking any
            // cached data between sessions, we clear the entire graphcache
            // store whenever the `users.masquerade` mutation succeeds.
            users(result, _args, cache) {
                if (result.users?.masquerade) {
                    // Invalidate the entire root Query so that all queries are
                    // refetched under the new masqueraded session.
                    const allFields = cache.inspectFields("Query");
                    for (const field of allFields) {
                        cache.invalidate("Query", field.fieldName, field.arguments || {});
                    }
                }
                // When a team member is created or updated, invalidate the
                // users query so GetTeamMembers refetches with fresh data.
                if (result.users?.createUser || result.users?.updateUser) {
                    cache
                        .inspectFields("Query")
                        .filter((f) => f.fieldName === "users")
                        .forEach((f) => cache.invalidate("Query", f.fieldName, f.arguments || {}));
                }
            },
            chatMessages(result, _args, cache, info) {
                const { sendMessage, editMessage, deleteMessage, pinMessage, unpinMessage, addReaction, removeReaction } = result.chatMessages ?? {};
                // Send Message: append to the cached message list for the room
                if (sendMessage) {
                    const newMsg = sendMessage;
                    const container = cache.resolve("Query", "chatMessages");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "messages")
                        : [];
                    if (newMsg.threadParentId) {
                        // Thread reply: invalidate the thread messages query
                        cache.invalidate(container, "threadMessages", {
                            threadParentId: newMsg.threadParentId
                        });
                    }
                    else {
                        // Top-level message: append to the room's message list
                        for (const f of fields) {
                            const vars = (f.arguments || {});
                            if (vars.chatRoomId !== newMsg.chatRoomId)
                                continue;
                            cache.updateQuery({
                                query: GetChatMessagesQuery,
                                variables: f.arguments
                            }, (data) => {
                                if (!data?.chatMessages?.messages)
                                    return data;
                                const existing = data.chatMessages.messages.messages ?? [];
                                const alreadyThere = existing.some((m) => m.chatMessageId === newMsg.chatMessageId);
                                if (alreadyThere)
                                    return data;
                                return {
                                    chatMessages: {
                                        ...data.chatMessages,
                                        messages: {
                                            ...data.chatMessages.messages,
                                            messages: [...existing, newMsg]
                                        }
                                    }
                                };
                            });
                        }
                    }
                    // Update the room's lastMessageAt in the sidebar
                    const roomContainer = cache.resolve("Query", "chatRooms");
                    const roomFields = roomContainer
                        ? cache
                            .inspectFields(roomContainer)
                            .filter((f) => f.fieldName === "rooms")
                        : [];
                    for (const f of roomFields) {
                        cache.updateQuery({
                            query: GetChatRoomsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.chatRooms)
                                return data;
                            const rooms = (data.chatRooms.rooms ?? []);
                            return {
                                chatRooms: {
                                    ...data.chatRooms,
                                    rooms: rooms.map((r) => r.chatRoomId === newMsg.chatRoomId
                                        ? { ...r, lastMessageAt: newMsg.createdAt }
                                        : r)
                                }
                            };
                        });
                    }
                }
                // Edit / Delete / Pin / Unpin / Reactions: invalidate the
                // affected room's message queries so they refetch.
                if (editMessage || deleteMessage || pinMessage || unpinMessage || addReaction || removeReaction) {
                    const vars = info?.variables;
                    const chatMessageId = vars?.chatMessageId ?? vars?.input?.chatMessageId;
                    const chatRoomId = editMessage?.chatRoomId
                        ?? pinMessage?.chatRoomId;
                    const container = cache.resolve("Query", "chatMessages");
                    if (container) {
                        const fields = cache.inspectFields(container);
                        for (const f of fields) {
                            cache.invalidate(container, f.fieldName, f.arguments || {});
                        }
                    }
                    // Also invalidate pinned messages queries
                    if (pinMessage || unpinMessage) {
                        if (container) {
                            cache.inspectFields(container)
                                .filter((f) => f.fieldName === "pinnedMessages")
                                .forEach((f) => cache.invalidate(container, f.fieldName, f.arguments || {}));
                        }
                    }
                }
            },
            chatRooms(result, _args, cache) {
                const { createRoom, updateRoom, archiveRoom, unarchiveRoom, leaveRoom } = result.chatRooms ?? {};
                if (createRoom) {
                    const newRoom = createRoom;
                    // Append the new room to every cached GetChatRooms
                    // result so the sidebar updates without a refetch.
                    const container = cache.resolve("Query", "chatRooms");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "rooms")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetChatRoomsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.chatRooms)
                                return data;
                            const existing = (data.chatRooms.rooms ?? []);
                            const alreadyThere = existing.some((r) => r.chatRoomId === newRoom.chatRoomId);
                            if (alreadyThere)
                                return data;
                            return {
                                chatRooms: {
                                    ...data.chatRooms,
                                    rooms: [newRoom, ...existing]
                                }
                            };
                        });
                    }
                }
                // Archive / Unarchive / Leave: invalidate room lists so
                // they refetch with the updated state.
                if (archiveRoom || unarchiveRoom || leaveRoom) {
                    const container = cache.resolve("Query", "chatRooms");
                    if (container) {
                        cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "rooms")
                            .forEach((f) => cache.invalidate(container, f.fieldName, f.arguments || {}));
                    }
                }
            },
            chatRoomGroups(result, _args, cache) {
                const { createGroup, deleteGroup, reorderGroups, assignRoom, unassignRoom } = result.chatRoomGroups ?? {};
                // Any group mutation invalidates cached group queries
                if (createGroup || deleteGroup || reorderGroups || assignRoom || unassignRoom) {
                    const container = cache.resolve("Query", "chatRoomGroups");
                    if (container) {
                        const fields = cache.inspectFields(container);
                        for (const f of fields) {
                            cache.invalidate(container, f.fieldName, f.arguments || {});
                        }
                    }
                }
            },
            organizations(result, _args, cache) {
                const { deleteOrganization } = result.organizations ?? {};
                if (deleteOrganization) {
                    const deletedId = deleteOrganization.organizationId;
                    const container = cache.resolve("Query", "organizations");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "organizations")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetOrganizationsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.organizations)
                                return data;
                            return {
                                organizations: {
                                    ...data.organizations,
                                    organizations: (data.organizations
                                        .organizations ?? []).filter((org) => org?.organizationId !==
                                        deletedId)
                                }
                            };
                        });
                    }
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "organizationCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetOrganizationCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.organizations)
                                return data;
                            const current = data.organizations.organizationCount ?? 0;
                            return {
                                organizations: {
                                    ...data.organizations,
                                    organizationCount: Math.max(0, current - 1)
                                }
                            };
                        });
                    }
                }
            },
            priorityViews(result, _args, cache) {
                const { deletePriorityView, bulkReorderPriorityViews } = result.priorityViews ?? {};
                // Delete Priority View
                if (deletePriorityView) {
                    const deletedId = deletePriorityView.priorityViewId;
                    cache.updateQuery({ query: GetPriorityViewsQuery }, (data) => {
                        if (!data?.priorityViews)
                            return data;
                        return {
                            ...data,
                            priorityViews: {
                                ...data.priorityViews,
                                priorityViews: (data.priorityViews.priorityViews ?? []).filter((v) => v?.priorityViewId !== deletedId)
                            }
                        };
                    });
                }
                if (bulkReorderPriorityViews) {
                    const newOrders = bulkReorderPriorityViews.filter(Boolean);
                    // Build a map of priorityViewId -> new sortOrder
                    const orderMap = new Map(newOrders.map((o) => [
                        o.priorityViewId,
                        o.sortOrder ?? 0
                    ]));
                    cache.updateQuery({ query: GetPriorityViewsQuery }, (data) => {
                        if (!data?.priorityViews)
                            return data;
                        const views = [
                            ...(data.priorityViews.priorityViews ?? [])
                        ];
                        // Apply the new sort orders
                        const updated = views.map((view) => {
                            const newSort = orderMap.get(view.priorityViewId);
                            return newSort !== undefined
                                ? { ...view, sortOrder: newSort }
                                : view;
                        });
                        // Re-sort by sortOrder
                        updated.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                        return {
                            ...data,
                            priorityViews: {
                                ...data.priorityViews,
                                priorityViews: updated
                            }
                        };
                    });
                }
            },
            priorityStatuses(result, _args, cache) {
                const { createPriorityStatus, bulkReorderPriorityStatuses, deleteAndReplacePriorityStatus } = result.priorityStatuses ?? {};
                // Helper: apply an updater to the priorityStatuses list
                // in both GetPriorityStatuses and GetPriorityViews
                const updateBothQueries = (updater) => {
                    cache.updateQuery({ query: GetPriorityStatusesQuery }, (data) => {
                        if (!data?.priorityStatuses)
                            return data;
                        return {
                            ...data,
                            priorityStatuses: {
                                ...data.priorityStatuses,
                                priorityStatuses: updater((data.priorityStatuses
                                    .priorityStatuses ??
                                    []))
                            }
                        };
                    });
                    cache.updateQuery({ query: GetPriorityViewsQuery }, (data) => {
                        if (!data?.priorityStatuses)
                            return data;
                        return {
                            ...data,
                            priorityStatuses: {
                                ...data.priorityStatuses,
                                priorityStatuses: updater((data.priorityStatuses
                                    .priorityStatuses ??
                                    []))
                            }
                        };
                    });
                    cache.updateQuery({
                        query: PriorityEntryModalQuery,
                        variables: { inOrganization: true }
                    }, (data) => {
                        if (!data?.priorityStatuses)
                            return data;
                        return {
                            ...data,
                            priorityStatuses: {
                                ...data.priorityStatuses,
                                priorityStatuses: updater((data.priorityStatuses
                                    .priorityStatuses ??
                                    []))
                            }
                        };
                    });
                };
                // Create
                if (createPriorityStatus) {
                    const newStatus = createPriorityStatus;
                    updateBothQueries((statuses) => {
                        // Guard against duplicates — graphcache may
                        // have already inserted the normalized entity.
                        const exists = statuses.some((s) => s.priorityStatusId ===
                            newStatus.priorityStatusId);
                        const updated = exists
                            ? statuses
                            : [...statuses, newStatus];
                        updated.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                        return updated;
                    });
                }
                // Bulk Reorder
                if (bulkReorderPriorityStatuses) {
                    const reordered = bulkReorderPriorityStatuses;
                    const orderMap = new Map(reordered
                        .filter(Boolean)
                        .map((s) => [
                        s.priorityStatusId,
                        s.sortOrder ?? 0
                    ]));
                    updateBothQueries((statuses) => {
                        const updated = statuses.map((s) => {
                            const newSort = orderMap.get(s.priorityStatusId);
                            return newSort !== undefined
                                ? { ...s, sortOrder: newSort }
                                : s;
                        });
                        updated.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                        return updated;
                    });
                }
                // Delete and Replace
                if (deleteAndReplacePriorityStatus) {
                    const deletedId = deleteAndReplacePriorityStatus.priorityStatusId;
                    updateBothQueries((statuses) => statuses.filter((s) => s.priorityStatusId !== deletedId));
                }
            },
            therapyCategories(result, _args, cache) {
                const { createTherapyCategory, deleteTherapyCategory } = result.therapyCategories ?? {};
                if (createTherapyCategory) {
                    const newCategory = createTherapyCategory;
                    const container = cache.resolve("Query", "therapyCategories");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "therapyCategories")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetTherapyCategoriesAndSubCategoriesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.therapyCategories)
                                return data;
                            return {
                                therapyCategories: {
                                    ...data.therapyCategories,
                                    therapyCategories: [
                                        newCategory,
                                        ...(data.therapyCategories
                                            .therapyCategories ?? [])
                                    ]
                                }
                            };
                        });
                    }
                }
                if (deleteTherapyCategory) {
                    const deletedId = deleteTherapyCategory.therapyCategoryId;
                    const container = cache.resolve("Query", "therapyCategories");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "therapyCategories")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetTherapyCategoriesAndSubCategoriesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.therapyCategories)
                                return data;
                            return {
                                therapyCategories: {
                                    ...data.therapyCategories,
                                    therapyCategories: (data.therapyCategories
                                        .therapyCategories ?? []).filter((tc) => tc?.therapyCategoryId !==
                                        deletedId)
                                }
                            };
                        });
                    }
                }
            },
            therapySubCategories(result, _args, cache) {
                const { createTherapySubCategory, deleteTherapySubCategory } = result.therapySubCategories ?? {};
                // Sub-categories are nested inside therapy categories in
                // GetTherapyCategoriesAndSubCategoriesQuery, and also
                // appear in GetTherapySubCategoryGroupsQuery. Invalidate
                // both so the UI refetches fresh data.
                const invalidateTherapyQueries = () => {
                    const catContainer = cache.resolve("Query", "therapyCategories");
                    if (catContainer) {
                        const fields = cache
                            .inspectFields(catContainer)
                            .filter((f) => f.fieldName === "therapyCategories");
                        for (const f of fields) {
                            cache.invalidate(catContainer, f.fieldName, f.arguments || {});
                        }
                    }
                    const subContainer = cache.resolve("Query", "therapySubCategories");
                    if (subContainer) {
                        const fields = cache
                            .inspectFields(subContainer)
                            .filter((f) => f.fieldName ===
                            "therapySubCategoryGroups");
                        for (const f of fields) {
                            cache.invalidate(subContainer, f.fieldName, f.arguments || {});
                        }
                    }
                };
                if (createTherapySubCategory) {
                    invalidateTherapyQueries();
                }
                if (deleteTherapySubCategory) {
                    invalidateTherapyQueries();
                }
            },
            conditions(result, _args, cache) {
                const { createCondition, deleteCondition } = result.conditions ?? {};
                if (createCondition) {
                    const newCondition = createCondition;
                    const container = cache.resolve("Query", "conditions");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "conditions")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetConditionsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.conditions)
                                return data;
                            return {
                                conditions: {
                                    ...data.conditions,
                                    conditions: [
                                        newCondition,
                                        ...(data.conditions
                                            .conditions ?? [])
                                    ]
                                }
                            };
                        });
                    }
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "conditionCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetConditionCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.conditions)
                                return data;
                            const current = data.conditions.conditionCount ?? 0;
                            return {
                                conditions: {
                                    ...data.conditions,
                                    conditionCount: current + 1
                                }
                            };
                        });
                    }
                }
                if (deleteCondition) {
                    const deletedId = deleteCondition
                        .conditionId;
                    const container = cache.resolve("Query", "conditions");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "conditions")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetConditionsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.conditions)
                                return data;
                            return {
                                conditions: {
                                    ...data.conditions,
                                    conditions: (data.conditions.conditions ??
                                        []).filter((c) => c?.conditionId !==
                                        deletedId)
                                }
                            };
                        });
                    }
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "conditionCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetConditionCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.conditions)
                                return data;
                            const current = data.conditions.conditionCount ?? 0;
                            return {
                                conditions: {
                                    ...data.conditions,
                                    conditionCount: Math.max(0, current - 1)
                                }
                            };
                        });
                    }
                }
            },
            insurances(result, _args, cache) {
                const { createInsurance, deleteInsurance } = result.insurances ?? {};
                if (createInsurance) {
                    const newInsurance = createInsurance;
                    const container = cache.resolve("Query", "insurances");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "insurances")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetInsurancesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.insurances)
                                return data;
                            return {
                                insurances: {
                                    ...data.insurances,
                                    insurances: [
                                        newInsurance,
                                        ...(data.insurances
                                            .insurances ?? [])
                                    ]
                                }
                            };
                        });
                    }
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "insuranceCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetInsuranceCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.insurances)
                                return data;
                            const current = data.insurances.insuranceCount ?? 0;
                            return {
                                insurances: {
                                    ...data.insurances,
                                    insuranceCount: current + 1
                                }
                            };
                        });
                    }
                }
                if (deleteInsurance) {
                    const deletedId = deleteInsurance
                        .insuranceId;
                    const container = cache.resolve("Query", "insurances");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "insurances")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetInsurancesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.insurances)
                                return data;
                            return {
                                insurances: {
                                    ...data.insurances,
                                    insurances: (data.insurances.insurances ??
                                        []).filter((ins) => ins?.insuranceId !==
                                        deletedId)
                                }
                            };
                        });
                    }
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "insuranceCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetInsuranceCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.insurances)
                                return data;
                            const current = data.insurances.insuranceCount ?? 0;
                            return {
                                insurances: {
                                    ...data.insurances,
                                    insuranceCount: Math.max(0, current - 1)
                                }
                            };
                        });
                    }
                }
            },
            postalCodes(result, _args, cache) {
                const { createPostalCode, deletePostalCode } = result.postalCodes ?? {};
                if (createPostalCode) {
                    const newPostalCode = createPostalCode;
                    const container = cache.resolve("Query", "postalCodes");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "postalCodes")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetPostalCodesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.postalCodes)
                                return data;
                            return {
                                postalCodes: {
                                    ...data.postalCodes,
                                    postalCodes: [
                                        newPostalCode,
                                        ...(data.postalCodes
                                            .postalCodes ?? [])
                                    ]
                                }
                            };
                        });
                    }
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "postalCodeCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetPostalCodeCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.postalCodes)
                                return data;
                            const current = data.postalCodes.postalCodeCount ?? 0;
                            return {
                                postalCodes: {
                                    ...data.postalCodes,
                                    postalCodeCount: current + 1
                                }
                            };
                        });
                    }
                }
                if (deletePostalCode) {
                    const deletedId = deletePostalCode
                        .postalCodeId;
                    const container = cache.resolve("Query", "postalCodes");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "postalCodes")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetPostalCodesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.postalCodes)
                                return data;
                            return {
                                postalCodes: {
                                    ...data.postalCodes,
                                    postalCodes: (data.postalCodes
                                        .postalCodes ?? []).filter((pc) => pc?.postalCodeId !==
                                        deletedId)
                                }
                            };
                        });
                    }
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "postalCodeCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetPostalCodeCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.postalCodes)
                                return data;
                            const current = data.postalCodes.postalCodeCount ?? 0;
                            return {
                                postalCodes: {
                                    ...data.postalCodes,
                                    postalCodeCount: Math.max(0, current - 1)
                                }
                            };
                        });
                    }
                }
            },
            locations(result, _args, cache) {
                const { createLocation, updateLocation, deleteLocation } = result.locations ?? {};
                // Create Location
                if (createLocation) {
                    const newLocation = createLocation;
                    const container = cache.resolve("Query", "locations");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "locations")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetLocationsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.locations)
                                return data;
                            return {
                                locations: {
                                    ...data.locations,
                                    locations: [
                                        newLocation,
                                        ...(data.locations.locations ?? [])
                                    ]
                                }
                            };
                        });
                    }
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "locationCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetLocationCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.locations)
                                return data;
                            const current = data.locations.locationCount ?? 0;
                            return {
                                locations: {
                                    ...data.locations,
                                    locationCount: current + 1
                                }
                            };
                        });
                    }
                }
                // Update Location
                if (updateLocation) {
                    const updated = updateLocation;
                    const container = cache.resolve("Query", "locations");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "locations")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetLocationsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.locations)
                                return data;
                            return {
                                locations: {
                                    ...data.locations,
                                    locations: (data.locations.locations ?? []).map((loc) => loc?.locationId ===
                                        updated.locationId
                                        ? updated
                                        : loc)
                                }
                            };
                        });
                    }
                }
                if (deleteLocation) {
                    const deletedId = deleteLocation.locationId;
                    const container = cache.resolve("Query", "locations");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "locations")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetLocationsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.locations)
                                return data;
                            return {
                                locations: {
                                    ...data.locations,
                                    locations: (data.locations.locations ?? []).filter((loc) => loc?.locationId !==
                                        deletedId)
                                }
                            };
                        });
                    }
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "locationCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetLocationCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.locations)
                                return data;
                            const current = data.locations.locationCount ?? 0;
                            return {
                                locations: {
                                    ...data.locations,
                                    locationCount: Math.max(0, current - 1)
                                }
                            };
                        });
                    }
                }
            },
            priorityInvites(result, _args, cache) {
                const { deletePriorityInvite } = result.priorityInvites ?? {};
                if (deletePriorityInvite) {
                    // Invalidate all cached priorityInvites queries so
                    // they refetch without the deleted invite.
                    const container = cache.resolve("Query", "priorityInvites");
                    if (container) {
                        const fields = cache.inspectFields(container);
                        for (const f of fields) {
                            cache.invalidate(container, f.fieldName, f.arguments || {});
                        }
                    }
                }
            },
            priorityEmailRecords(result, _args, cache) {
                const { deletePriorityEmailRecord } = result.priorityEmailRecords ?? {};
                if (deletePriorityEmailRecord) {
                    // Invalidate all cached priorityEmailRecords queries
                    // so they refetch without the deleted record.
                    const container = cache.resolve("Query", "priorityEmailRecords");
                    if (container) {
                        const fields = cache.inspectFields(container);
                        for (const f of fields) {
                            cache.invalidate(container, f.fieldName, f.arguments || {});
                        }
                    }
                }
            },
            files(result, _args, cache) {
                const { createFileRecord, updateFileRecord, deleteFile } = result.files ?? {};
                // Create File
                if (createFileRecord) {
                    const newFile = createFileRecord;
                    // Update every cached files list regardless of search/limit/offset
                    const container = cache.resolve("Query", "files");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "files")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetFilesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.files)
                                return data;
                            return {
                                files: {
                                    ...data.files,
                                    files: [
                                        newFile,
                                        ...(data.files.files ?? [])
                                    ]
                                }
                            };
                        });
                    }
                    // Increment any cached file counts for matching searches
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "fileCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetFilesCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.files)
                                return data;
                            const current = data.files.fileCount ?? 0;
                            return {
                                files: {
                                    ...data.files,
                                    fileCount: current + 1
                                }
                            };
                        });
                    }
                }
                // Update File
                if (updateFileRecord) {
                    const updated = updateFileRecord;
                    // Update every cached files list regardless of search/limit/offset
                    const container = cache.resolve("Query", "files");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "files")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetFilesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.files)
                                return data;
                            return {
                                files: {
                                    ...data.files,
                                    files: (data.files.files ?? []).map((file) => file?.fileId === updated.fileId
                                        ? updated
                                        : file)
                                }
                            };
                        });
                    }
                }
                // Delete File
                if (deleteFile) {
                    const deletedId = deleteFile;
                    // Update every cached files list regardless of search/limit/offset
                    const container = cache.resolve("Query", "files");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "files")
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetFilesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.files)
                                return data;
                            return {
                                files: {
                                    ...data?.files,
                                    files: (data?.files?.files ?? []).filter((file) => file?.fileId !== deletedId)
                                }
                            };
                        });
                    }
                    // Decrement any cached file counts for matching searches
                    const countFields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "fileCount")
                        : [];
                    for (const f of countFields) {
                        cache.updateQuery({
                            query: GetFilesCountQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.files)
                                return data;
                            const current = data.files.fileCount ?? 0;
                            return {
                                files: {
                                    ...data.files,
                                    fileCount: Math.max(0, current - 1)
                                }
                            };
                        });
                    }
                }
            },
            priorityReferrals(result, _args, cache) {
                const { archivePriorityReferral, movePriorityReferralToPriorityEntry, restorePriorityReferral } = result.priorityReferrals ?? {};
                // Archive Priority Referral
                if (archivePriorityReferral) {
                    // Update every cached priorityReferrals list with archived:false (any search/limit/offset)
                    const archivedId = archivePriorityReferral.priorityReferralId;
                    const container = cache.resolve("Query", "priorityReferrals");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "priorityReferrals" &&
                            f.arguments?.archived === false)
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetReferralsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.priorityReferrals)
                                return data;
                            return {
                                priorityReferrals: {
                                    ...data.priorityReferrals,
                                    priorityReferrals: (data.priorityReferrals
                                        .priorityReferrals ?? []).filter((r) => r?.priorityReferralId !==
                                        archivedId)
                                }
                            };
                        });
                    }
                    // Decrement the non-archived referrals count
                    cache.updateQuery({
                        query: GetReferralCountQuery,
                        variables: { archived: false }
                    }, (data) => {
                        if (!data?.priorityReferrals)
                            return data;
                        const current = data.priorityReferrals.priorityReferralCount ??
                            0;
                        return {
                            priorityReferrals: {
                                ...data.priorityReferrals,
                                priorityReferralCount: Math.max(0, current - 1)
                            }
                        };
                    });
                    // Decrement the subscription counts
                    cache.updateQuery({ query: GetSubScriptionCountsQuery }, (data) => {
                        if (!data?.priorityReferrals)
                            return data;
                        const current = data.priorityReferrals.priorityReferralCount ??
                            0;
                        return {
                            priorityReferrals: {
                                ...data.priorityReferrals,
                                priorityReferralCount: Math.max(0, current - 1)
                            }
                        };
                    });
                }
                // Move Priority Referral to Priority Entry
                if (movePriorityReferralToPriorityEntry) {
                    // Update every cached priorityReferrals list with archived:false (any search/limit/offset)
                    const movedId = movePriorityReferralToPriorityEntry.priorityReferralId;
                    const container = cache.resolve("Query", "priorityReferrals");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "priorityReferrals" &&
                            f.arguments?.archived === false)
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetReferralsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.priorityReferrals)
                                return data;
                            return {
                                priorityReferrals: {
                                    ...data.priorityReferrals,
                                    priorityReferrals: (data.priorityReferrals
                                        .priorityReferrals ?? []).filter((r) => r?.priorityReferralId !==
                                        movedId)
                                }
                            };
                        });
                    }
                    // Decrement the non-archived referrals count, since it was moved to the queue
                    cache.updateQuery({
                        query: GetReferralCountQuery,
                        variables: { archived: false }
                    }, (data) => {
                        if (!data?.priorityReferrals)
                            return data;
                        const current = data.priorityReferrals.priorityReferralCount ??
                            0;
                        return {
                            priorityReferrals: {
                                ...data.priorityReferrals,
                                priorityReferralCount: Math.max(0, current - 1)
                            }
                        };
                    });
                    // Decrement the subscription counts, since it was moved to the queue
                    cache.updateQuery({ query: GetSubScriptionCountsQuery }, (data) => {
                        if (!data?.priorityReferrals)
                            return data;
                        const current = data.priorityReferrals.priorityReferralCount ??
                            0;
                        return {
                            priorityReferrals: {
                                ...data.priorityReferrals,
                                priorityReferralCount: Math.max(0, current - 1)
                            }
                        };
                    });
                }
                // Restore Priority Referral
                if (restorePriorityReferral) {
                    const restoredId = restorePriorityReferral.priorityReferralId;
                    // Update every cached priorityReferrals list with archived:false (any search/limit/offset)
                    const container = cache.resolve("Query", "priorityReferrals");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "priorityReferrals" &&
                            f.arguments?.archived === true)
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetReferralsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.priorityReferrals)
                                return data;
                            return {
                                priorityReferrals: {
                                    ...data.priorityReferrals,
                                    priorityReferrals: (data.priorityReferrals
                                        .priorityReferrals ?? []).filter((r) => r?.priorityReferralId !==
                                        restoredId)
                                }
                            };
                        });
                    }
                    // Increment the non-archived referrals count
                    cache.updateQuery({
                        query: GetReferralCountQuery,
                        variables: { archived: false }
                    }, (data) => {
                        if (!data?.priorityReferrals)
                            return data;
                        const current = data.priorityReferrals.priorityReferralCount ??
                            0;
                        return {
                            priorityReferrals: {
                                ...data.priorityReferrals,
                                priorityReferralCount: current + 1
                            }
                        };
                    });
                    // Increment the subscription counts
                    cache.updateQuery({ query: GetSubScriptionCountsQuery }, (data) => {
                        if (!data?.priorityReferrals)
                            return data;
                        const current = data.priorityReferrals.priorityReferralCount ??
                            0;
                        return {
                            priorityReferrals: {
                                ...data.priorityReferrals,
                                priorityReferralCount: current + 1
                            }
                        };
                    });
                }
            },
            websiteLeads(result, _args, cache) {
                const { archiveWebsiteLead, moveWebsiteLeadToPriorityEntry, restoreWebsiteLead } = result.websiteLeads ?? {};
                // Archive Website Lead
                if (archiveWebsiteLead) {
                    // Update every cached websiteLeads list with archived:false (any search/limit/offset)
                    const archivedId = archiveWebsiteLead.websiteLeadId;
                    const container = cache.resolve("Query", "websiteLeads");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "websiteLeads" &&
                            f.arguments?.archived === false)
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetWebsiteLeadsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.websiteLeads)
                                return data;
                            return {
                                websiteLeads: {
                                    ...data.websiteLeads,
                                    websiteLeads: (data.websiteLeads.websiteLeads ?? []).filter((wl) => wl?.websiteLeadId !== archivedId)
                                }
                            };
                        });
                    }
                    // Decrement the non-archived website leads count
                    cache.updateQuery({
                        query: GetWebsiteLeadCountQuery,
                        variables: { archived: false }
                    }, (data) => {
                        if (!data?.websiteLeads)
                            return data;
                        const current = data.websiteLeads.websiteLeadCount ?? 0;
                        return {
                            websiteLeads: {
                                ...data.websiteLeads,
                                websiteLeadCount: Math.max(0, current - 1)
                            }
                        };
                    });
                    // Decrement the subscription counts
                    cache.updateQuery({ query: GetSubScriptionCountsQuery }, (data) => {
                        if (!data?.websiteLeads)
                            return data;
                        const current = data.websiteLeads.websiteLeadCount ?? 0;
                        return {
                            websiteLeads: {
                                ...data.websiteLeads,
                                websiteLeadCount: Math.max(0, current - 1)
                            }
                        };
                    });
                }
                // Move Website Lead to Priority Entry
                if (moveWebsiteLeadToPriorityEntry) {
                    // Update every cached websiteLeads list with archived:false (any search/limit/offset)
                    const movedId = moveWebsiteLeadToPriorityEntry.websiteLeadId;
                    const container = cache.resolve("Query", "websiteLeads");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "websiteLeads" &&
                            f.arguments?.archived === false)
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetWebsiteLeadsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.websiteLeads)
                                return data;
                            return {
                                websiteLeads: {
                                    ...data.websiteLeads,
                                    websiteLeads: (data.websiteLeads.websiteLeads ?? []).filter((wl) => wl?.websiteLeadId !== movedId)
                                }
                            };
                        });
                    }
                    // Decrement the non-archived website leads count, since it was moved to the queue
                    cache.updateQuery({
                        query: GetWebsiteLeadCountQuery,
                        variables: { archived: false }
                    }, (data) => {
                        if (!data?.websiteLeads)
                            return data;
                        const current = data.websiteLeads.websiteLeadCount ?? 0;
                        return {
                            websiteLeads: {
                                ...data.websiteLeads,
                                websiteLeadCount: Math.max(0, current - 1)
                            }
                        };
                    });
                    // Decrement the subscription counts, since it was moved to the queue
                    cache.updateQuery({ query: GetSubScriptionCountsQuery }, (data) => {
                        if (!data?.websiteLeads)
                            return data;
                        const current = data.websiteLeads.websiteLeadCount ?? 0;
                        return {
                            websiteLeads: {
                                ...data.websiteLeads,
                                websiteLeadCount: Math.max(0, current - 1)
                            }
                        };
                    });
                }
                // Restore Website Lead
                if (restoreWebsiteLead) {
                    const restoredId = restoreWebsiteLead.websiteLeadId;
                    // Update every cached websiteLeads list with archived:true (any search/limit/offset)
                    const container = cache.resolve("Query", "websiteLeads");
                    const fields = container
                        ? cache
                            .inspectFields(container)
                            .filter((f) => f.fieldName === "websiteLeads" &&
                            f.arguments?.archived === true)
                        : [];
                    for (const f of fields) {
                        cache.updateQuery({
                            query: GetWebsiteLeadsQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.websiteLeads)
                                return data;
                            return {
                                websiteLeads: {
                                    ...data.websiteLeads,
                                    websiteLeads: (data.websiteLeads.websiteLeads ?? []).filter((wl) => wl?.websiteLeadId !== restoredId)
                                }
                            };
                        });
                    }
                    // Increment the non-archived website leads count
                    cache.updateQuery({
                        query: GetWebsiteLeadCountQuery,
                        variables: { archived: false }
                    }, (data) => {
                        if (!data?.websiteLeads)
                            return data;
                        const current = data.websiteLeads.websiteLeadCount ?? 0;
                        return {
                            websiteLeads: {
                                ...data.websiteLeads,
                                websiteLeadCount: current + 1
                            }
                        };
                    });
                    // Increment the subscription counts
                    cache.updateQuery({ query: GetSubScriptionCountsQuery }, (data) => {
                        if (!data?.websiteLeads)
                            return data;
                        const current = data.websiteLeads.websiteLeadCount ?? 0;
                        return {
                            websiteLeads: {
                                ...data.websiteLeads,
                                websiteLeadCount: current + 1
                            }
                        };
                    });
                }
            },
            priorityNotes(result, _args, cache, info) {
                const newNote = (result.priorityNotes?.createPriorityNote ??
                    null);
                if (!newNote)
                    return;
                // This mutation's variables include $input, even though Mutation.priorityNotes has no args.
                const input = info
                    ?.variables?.input;
                const caregiverId = input?.caregiverId;
                const patientId = input?.patientId;
                if (!caregiverId || !patientId)
                    return;
                const container = cache.resolve("Query", "priorityNotes");
                const fields = container
                    ? cache
                        .inspectFields(container)
                        .filter((f) => f.fieldName === "priorityNotes")
                    : [];
                for (const f of fields) {
                    const vars = (f.arguments || {});
                    const search = vars.search;
                    if (vars.caregiverId !== caregiverId)
                        continue;
                    if (vars.patientId !== patientId)
                        continue;
                    // If the UI is currently showing a searched list, don't inject results that might not match.
                    if (typeof search === "string" && search.trim().length > 0)
                        continue;
                    cache.updateQuery({
                        query: GetPriorityNotesQuery,
                        variables: vars
                    }, (data) => {
                        if (!data?.priorityNotes)
                            return data;
                        const existing = data.priorityNotes.priorityNotes ?? [];
                        const alreadyThere = existing.some((n) => n?.priorityNoteId === newNote.priorityNoteId);
                        if (alreadyThere)
                            return data;
                        return {
                            priorityNotes: {
                                ...data.priorityNotes,
                                priorityNotes: [newNote, ...existing]
                            }
                        };
                    });
                }
            }
        },
        Subscription: {
            priorityReferralReceived(result, _args, cache) {
                const newReferral = result?.priorityReferralReceived;
                if (!newReferral)
                    return;
                // Update every cached priorityReferrals list with archived:false (any search/limit/offset)
                const container = cache.resolve("Query", "priorityReferrals");
                const fields = container
                    ? cache
                        .inspectFields(container)
                        .filter((f) => f.fieldName === "priorityReferrals" &&
                        f.arguments?.archived === false)
                    : [];
                for (const f of fields) {
                    cache.updateQuery({
                        query: GetReferralsQuery,
                        variables: f.arguments
                    }, (data) => {
                        if (!data?.priorityReferrals)
                            return data;
                        return {
                            priorityReferrals: {
                                ...data.priorityReferrals,
                                priorityReferrals: [
                                    newReferral,
                                    ...(data.priorityReferrals
                                        .priorityReferrals ?? [])
                                ]
                            }
                        };
                    });
                }
                // Update the priority referrals count
                cache.updateQuery({
                    query: GetReferralCountQuery,
                    variables: { archived: false }
                }, (data) => {
                    if (!data?.priorityReferrals)
                        return data;
                    const current = data.priorityReferrals.priorityReferralCount ?? 0;
                    return {
                        priorityReferrals: {
                            ...data.priorityReferrals,
                            priorityReferralCount: current + 1
                        }
                    };
                });
                // Update the subscription counts
                cache.updateQuery({ query: GetSubScriptionCountsQuery }, (data) => {
                    if (!data?.priorityReferrals)
                        return data;
                    const current = data.priorityReferrals.priorityReferralCount ?? 0;
                    return {
                        priorityReferrals: {
                            ...data.priorityReferrals,
                            priorityReferralCount: current + 1
                        }
                    };
                });
            },
            websiteLeadReceived(result, _args, cache) {
                const newLead = result?.websiteLeadReceived;
                if (!newLead)
                    return;
                // Update every cached websiteLeads list with archived:false (any search/limit/offset)
                const container = cache.resolve("Query", "websiteLeads");
                const fields = container
                    ? cache
                        .inspectFields(container)
                        .filter((f) => f.fieldName === "websiteLeads" &&
                        f.arguments?.archived === false)
                    : [];
                for (const f of fields) {
                    cache.updateQuery({
                        query: GetWebsiteLeadsQuery,
                        variables: f.arguments
                    }, (data) => {
                        if (!data?.websiteLeads)
                            return data;
                        return {
                            websiteLeads: {
                                ...data.websiteLeads,
                                websiteLeads: [
                                    newLead,
                                    ...(data.websiteLeads.websiteLeads ??
                                        [])
                                ]
                            }
                        };
                    });
                }
                // Update the website leads count
                cache.updateQuery({
                    query: GetWebsiteLeadCountQuery,
                    variables: { archived: false }
                }, (data) => {
                    if (!data?.websiteLeads)
                        return data;
                    const current = data.websiteLeads.websiteLeadCount ?? 0;
                    return {
                        websiteLeads: {
                            ...data.websiteLeads,
                            websiteLeadCount: current + 1
                        }
                    };
                });
                // Update the subscription counts
                cache.updateQuery({ query: GetSubScriptionCountsQuery }, (data) => {
                    if (!data?.websiteLeads)
                        return data;
                    const current = data.websiteLeads.websiteLeadCount ?? 0;
                    return {
                        websiteLeads: {
                            ...data.websiteLeads,
                            websiteLeadCount: current + 1
                        }
                    };
                });
            },
            chatMessageReceived(result, _args, cache) {
                const newMsg = result?.chatMessageReceived;
                if (!newMsg)
                    return;
                const container = cache.resolve("Query", "chatMessages");
                const fields = container
                    ? cache
                        .inspectFields(container)
                        .filter((f) => f.fieldName === "messages")
                    : [];
                if (newMsg.threadParentId) {
                    // Thread reply: invalidate the thread messages query so it refetches
                    cache.invalidate(container, "threadMessages", {
                        threadParentId: newMsg.threadParentId
                    });
                }
                else {
                    // Top-level message: append to the room's cached message list
                    for (const f of fields) {
                        const vars = (f.arguments || {});
                        if (vars.chatRoomId !== newMsg.chatRoomId)
                            continue;
                        cache.updateQuery({
                            query: GetChatMessagesQuery,
                            variables: f.arguments
                        }, (data) => {
                            if (!data?.chatMessages?.messages)
                                return data;
                            const existing = data.chatMessages.messages.messages ?? [];
                            const alreadyThere = existing.some((m) => m.chatMessageId === newMsg.chatMessageId);
                            if (alreadyThere)
                                return data;
                            return {
                                chatMessages: {
                                    ...data.chatMessages,
                                    messages: {
                                        ...data.chatMessages.messages,
                                        messages: [...existing, newMsg]
                                    }
                                }
                            };
                        });
                    }
                }
                // Update the room's lastMessageAt in the sidebar
                const roomContainer = cache.resolve("Query", "chatRooms");
                const roomFields = roomContainer
                    ? cache
                        .inspectFields(roomContainer)
                        .filter((f) => f.fieldName === "rooms")
                    : [];
                for (const f of roomFields) {
                    cache.updateQuery({
                        query: GetChatRoomsQuery,
                        variables: f.arguments
                    }, (data) => {
                        if (!data?.chatRooms)
                            return data;
                        const rooms = (data.chatRooms.rooms ?? []);
                        return {
                            chatRooms: {
                                ...data.chatRooms,
                                rooms: rooms.map((r) => r.chatRoomId === newMsg.chatRoomId
                                    ? { ...r, lastMessageAt: newMsg.createdAt }
                                    : r)
                            }
                        };
                    });
                }
            },
            chatMessageUpdated(result, _args, cache) {
                const update = result?.chatMessageUpdated;
                if (!update)
                    return;
                // Invalidate all cached message queries so they refetch
                // with the updated message state (edited, deleted, pinned, reactions, etc.)
                const container = cache.resolve("Query", "chatMessages");
                if (container) {
                    const fields = cache.inspectFields(container);
                    for (const f of fields) {
                        cache.invalidate(container, f.fieldName, f.arguments || {});
                    }
                }
            },
            chatRoomUpdated(result, _args, cache) {
                const update = result?.chatRoomUpdated;
                if (!update?.room)
                    return;
                // Invalidate cached room queries so the sidebar refetches
                // with updated room metadata (members, name, archive state, etc.)
                const container = cache.resolve("Query", "chatRooms");
                if (container) {
                    cache
                        .inspectFields(container)
                        .filter((f) => f.fieldName === "rooms")
                        .forEach((f) => cache.invalidate(container, f.fieldName, f.arguments || {}));
                }
            }
        }
    }
});
